/**
* @license Apache-2.0
*
* Copyright (c) 2020 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import stride2offset from '@stdlib/strided/base/stride2offset';
import ndarray from './ndarray.js';


// MAIN //

/**
* Computes the cumulative minimum absolute value of a strided array.
*
* @param {PositiveInteger} N - number of indexed elements
* @param {NumericArray} x - input array
* @param {integer} strideX - stride length for `x`
* @param {NumericArray} y - output array
* @param {integer} strideY - stride length for `y`
* @returns {NumericArray} output array
*
* @example
* var x = [ 1.0, -2.0, 2.0 ];
* var y = [ 0.0, 0.0, 0.0 ];
* var N = x.length;
*
* var v = cuminabs( N, x, 1, y, 1 );
* // returns [ 1.0, 1.0, 1.0 ]
*/
function cuminabs( N, x, strideX, y, strideY ) {
	var ix = stride2offset( N, strideX );
	var iy = stride2offset( N, strideY );
	return ndarray( N, x, strideX, ix, y, strideY, iy );
}


// EXPORTS //

export default cuminabs;
